import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CmsBannerComponent, CmsService } from '@spartacus/core';
import { CmsComponentData, ICON_TYPE } from '@spartacus/storefront';
import { BehaviorSubject, combineLatest, interval, Observable, of, Subscription } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';

// Keep in sync with the mobile breakpoint used in atg-homepage-image-carousel.component.scss
const MOBILE_BREAKPOINT = '(max-width: 767px)';

export interface CmsRotatingImagesComponent {
  uid?: string;
  title?: string;
  effect?: string;
  banners?: string;           // ATG OCC field name
  bannerComponents?: string;  // Spartacus standard field name (fallback)
}

// CMS sends styleClasses like "banner-left-centre-align" / "banner-right-middle-align".
// Maps to a single "pos-{h}-{v}" class consumed by the .banner-headline overlay styles.
const HEADLINE_ALIGN_PATTERN = /banner-([a-z]+)-([a-z]+)-align/i;

const HEADLINE_HORIZONTAL_MAP: Record<string, string> = {
  left: 'left',
  center: 'center',
  right: 'right',
};

const HEADLINE_VERTICAL_MAP: Record<string, string> = {
  top: 'top',
  middle: 'middle',
  bottom: 'bottom',
};

@Component({
  selector: 'atg-homepage-image-carousel',
  templateUrl: './atg-homepage-image-carousel.component.html',
  styleUrls: ['./atg-homepage-image-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AtgHomepageImageCarouselComponent implements OnDestroy {
  private readonly AUTO_PLAY_INTERVAL = 5000;
  private readonly MULTI_IMAGE_CHUNK = 3;
  previousIcon = ICON_TYPE.CARET_LEFT;
  nextIcon = ICON_TYPE.CARET_RIGHT;

  private activeIndex$ = new BehaviorSubject<number>(0);
  readonly currentIndex$: Observable<number> = this.activeIndex$.asObservable();

  private readonly data$ = this.componentData.data$.pipe(
    filter(Boolean),
    map((data) => {
      return data;
    })
  );

  // Title shows independently — no dependency on banner loading
  readonly title$: Observable<string | undefined> = this.data$.pipe(
    map((data) => {
      const title = data.title?.trim();
      return title;
    }),
    startWith(undefined)
  );

  // Section5 has title; Section1 doesn't
  // Also check uid as fallback: "AtgHomepageImageCarouselComponent" = Section5 (multi-image)
  readonly isMultiImage$: Observable<boolean> = this.data$.pipe(
    map((data) => {
      const hasTitle = !!(data.title && data.title.trim());
      const isSection5 = data.uid === 'AtgHomepageImageCarouselComponent';
      return hasTitle || isSection5;
    }),
    startWith(false)
  );

  // Fetch individual banner components by UID (Spartacus way: CmsService.getComponentData)
  // Use startWith(null) to prevent combineLatest from blocking
  private readonly allBanners$: Observable<CmsBannerComponent[]> = this.data$.pipe(
    map((data) => (data.banners ?? data.bannerComponents ?? '').trim().split(' ').filter(Boolean)),
    switchMap((uids) =>
      uids.length
        ? combineLatest(
            uids.map((uid) =>
              this.cmsService.getComponentData<CmsBannerComponent>(uid).pipe(
                startWith(null as CmsBannerComponent | null)
              )
            )
          ).pipe(
            map((banners) => banners.filter((b): b is CmsBannerComponent => b !== null))
          )
        : of([])
    ),
    startWith([])
  );

  // Below the mobile breakpoint, always show one image per slide regardless of section
  private readonly isMobile$: Observable<boolean> = this.breakpointObserver
    .observe(MOBILE_BREAKPOINT)
    .pipe(map((state) => state.matches));

  // Carousel data: only emit when we have actual slides (avoid empty state flicker)
  readonly carouselData$: Observable<{
    slides: CmsBannerComponent[][];
    isMultiImage: boolean;
  }> = combineLatest([this.allBanners$, this.isMultiImage$, this.isMobile$]).pipe(
    map(([banners, isMulti, isMobile]) => {
      const isMultiImage = isMulti && !isMobile;
      const size = isMultiImage ? this.MULTI_IMAGE_CHUNK : 1;
      const slides: CmsBannerComponent[][] = [];
      for (let i = 0; i < banners.length; i += size) {
        slides.push(banners.slice(i, i + size));
      }
      return { slides, isMultiImage };
    }),
    filter((data) => {
      const hasSlides = data.slides.length > 0;
      return hasSlides;
    }),
    tap((data) => {
      if (this.activeIndex$.value >= data.slides.length) {
        this.activeIndex$.next(0);
      }
    })
  );

  private autoPlaySub: Subscription;

  constructor(
    protected componentData: CmsComponentData<CmsRotatingImagesComponent>,
    protected cmsService: CmsService,
    protected breakpointObserver: BreakpointObserver
  ) {
    this.autoPlaySub = combineLatest([this.carouselData$, interval(this.AUTO_PLAY_INTERVAL)]).subscribe(
      ([data]) => {
        if (data.slides.length > 1) {
          this.activeIndex$.next((this.activeIndex$.value + 1) % data.slides.length);
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.autoPlaySub.unsubscribe();
  }

  prev(total: number): void {
    this.activeIndex$.next((this.activeIndex$.value - 1 + total) % total);
  }

  next(total: number): void {
    this.activeIndex$.next((this.activeIndex$.value + 1) % total);
  }

  goTo(index: number): void {
    this.activeIndex$.next(index);
  }

  // Derives the headline overlay position (e.g. "pos-left-top") from the banner's CMS styleClasses.
  // Returns '' when styleClasses is missing/unrecognized so the headline keeps its default in-flow layout.
  getHeadlineAlignmentClass(banner: CmsBannerComponent): string {
    const match = banner.styleClasses?.toLowerCase().match(HEADLINE_ALIGN_PATTERN);
    if (!match) {
      return '';
    }
    const horizontal = HEADLINE_HORIZONTAL_MAP[match[1]];
    const vertical = HEADLINE_VERTICAL_MAP[match[2]];
    return horizontal && vertical ? `pos-${horizontal}-${vertical}` : '';
  }
}
